import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { ConfigurationService } from '@treasury/core/config';
import { NavigationService } from '@treasury/core/navigation';
import { TmBaseComponent } from '@treasury/presentation';
import { arrowBackIcon, dashboardIcon, menuIcon } from '@treasury/presentation/assets/icons';
import '@treasury/presentation/components/tm-fdic-digital-sign';
import { type Action } from '@treasury/utils';
import { type TemplateResult, css, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { HeaderBarService } from '../services/jhd-header-bar.service';
import { LayoutService } from '../services/layout.service';
import { MenuItem } from '../services/types/jhd-header-bar.type';

export const tagName = 'jhd-header-bar';
@customElement(tagName)
export class JhdHeaderBar extends TmBaseComponent {
    @InjectProperty()
    declare private layoutService: LayoutService;

    @InjectProperty()
    declare private headerService: HeaderBarService;

    @InjectProperty()
    declare private navigationService: NavigationService;

    @InjectProperty()
    declare private config: ConfigurationService;

    @state()
    private backAction?: Action;

    @state()
    private pageTitle: string | TemplateResult = '';

    @state()
    private subtitle = '';

    @state()
    private menuItems: MenuItem[] = [];

    @state()
    private isSidebarOpen = false;

    @state()
    private empty = false;

    @state()
    private hideHeader = false;

    @state()
    private enableFdicSign = false;

    protected firstUpdated() {
        this.layoutService.isSidebarOpen$.subscribe(isSidebarOpen => {
            this.isSidebarOpen = isSidebarOpen;
        });

        this.headerService.config$.subscribe(
            ({ backAction, title, subtitle, menuItems, empty, hideHeader, enableFdicSign }) => {
                this.backAction = backAction;
                this.pageTitle = title;
                this.subtitle = subtitle;
                this.menuItems = menuItems;
                this.empty = empty;
                this.hideHeader = hideHeader;
                this.enableFdicSign = enableFdicSign;
            },
            true
        );
    }

    protected render() {
        return html`
            <div
                class=${classMap({
                    'header-bar z-40 pt-[--safe-area-top]': true,
                    sticky: !this.empty,
                    absolute: this.empty,
                    'bg-header': !this.empty,
                    'bg-transparent': this.empty,
                    hidden: this.hideHeader,
                })}
            >
                <div
                    class=${classMap({
                        'flex flex-row items-center h-[--navbar-height] p-[--navbar-padding] text-gray-0': true,
                        'justify-start': !this.backAction,
                        'justify-between': !!this.backAction,
                    })}
                >
                    ${this.renderAction()}${this.renderTitle()}${this.renderMenuItems()}
                </div>
                ${this.enableFdicSign
                    ? html`<tm-fdic-digital-sign
                          reversed
                          container="px-4 py-2"
                      ></tm-fdic-digital-sign>`
                    : nothing}
            </div>
        `;
    }

    private renderAction() {
        const action = this.backAction
            ? html`<button
                  id="back-button"
                  class="flex cursor-pointer border-none outline-none p-2"
                  @click=${this.backAction}
              >
                  <tm-icon .icon=${arrowBackIcon} color="#fff"></tm-icon>
              </button>`
            : html`<div
                  class="toggle-container relative w-6 h-6 md:hidden"
                  @click=${() => this.layoutService.toggleSidebar()}
                  @keyup=${(e: KeyboardEvent) =>
                      e.key === 'Return' && this.layoutService.toggleSidebar()}
              >
                  <tm-icon
                      .icon=${menuIcon}
                      color=${this.empty ? 'var(--header-text-color)' : '#fff'}
                      class=${classMap({
                          'toggle-icon': true,
                          empty: this.empty,
                      })}
                  ></tm-icon>
              </div>`;
        return html`<div class="action-container flex items-center cursor-pointer z-10">
            ${action}
        </div>`;
    }

    private renderTitle() {
        const subtitle = this.subtitle
            ? html`<div class="text-xs">${this.subtitle}</div>`
            : nothing;
        return html`
            <div
                class=${classMap({
                    grow: true,
                })}
            >
                <div
                    class=${classMap({
                        'flex md:mx-0 px-3': true,
                        'justify-center': !!this.backAction,
                    })}
                >
                    <img
                        src="/branding/${this.config.institutionId}/logo.webp"
                        alt="logo"
                        class="logo h-8 bg-white"
                    />
                    <div class="spacer px-3 pt-1">|</div>
                    <div class="text-center">
                        <div class="text-lg font-medium leading-6">${this.pageTitle}</div>
                        ${subtitle}
                    </div>
                </div>
            </div>
        `;
    }

    private renderMenuItems() {
        const menuItems = this.menuItems?.map(item => {
            const visible = item.visible ? item.visible() : true;
            return visible
                ? html`
                      <button
                          title=${item.title}
                          class="header-button inline-block relative cursor-pointer border-none outline-none p-2 last-of-type:mr-0 focus:outline-none"
                          @click=${() => item.action()}
                      >
                          ${item.icon}
                      </button>
                  `
                : nothing;
        });
        if (this.backAction) {
            menuItems.push(
                html`<button
                    title="Return to dashboard"
                    class="header-button inline-block relative cursor-pointer border-none outline-none p-2 last-of-type:mr-0 focus:outline-none"
                    @click=${() => this.navigationService.navigate('/dashboard')}
                >
                    <tm-icon .icon=${dashboardIcon} color="var(--jh-color-gray-0)"></tm-icon>
                </button>`
            );
        }
        return html`<div class="header-actions flex flex-0 justify-end">${menuItems}</div>`;
    }

    public static get styles() {
        return [
            css`
                :host {
                    margin-block-start: 0em;
                    margin-block-end: 0px;
                }
                @media (max-width: 500px) {
                    img {
                        display: none;
                    }
                    .spacer {
                        display: none;
                    }
                }
            `,
        ];
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: JhdHeaderBar;
    }
}
